import { useDimensions } from "@repo/core/hooks/useDimensions";
import { useMemo, useRef, useState } from "react";
import { useNewTransactionStore } from "@repo/core/stores/newTransaction";
import { useNewTransactionFlow } from "@repo/core/hooks/useNewTransactionFlow";
import { IModalRefProps } from "@repo/core/components/layout/Modal/types";
import {
  applyMaskToCurrencyValue,
  removeMaskToCurrencyValue
} from "@repo/core/utils/mask.utils";
import {
  FidelityOptions,
  mockFidelityDataInfo,
  mockFidelityOptions
} from "./mock";
import { TransactionFlowType } from "@repo/core/screens/NewTransaction/types";

export const NewTransactionFidelityController = () => {
  // Hooks
  const { navigateBack, navigateToNext, currentRouteConfig } =
    useNewTransactionFlow();
  const { isMobile } = useDimensions();
  const modalRef = useRef<IModalRefProps>(null);

  // Store
  const {
    flowType,
    setBonusValue,
    setAvailableBonus,
    transactionValue,
    bonusValue,
    availableBonus,
    discountedTransactionValue,
    setDiscountedTransactionValue
  } = useNewTransactionStore();

  // Local State
  const [inputBonusValue, setInputBonusValue] = useState(
    bonusValue ? bonusValue.toString() : "0"
  );
  const [inputBonusValueMasked, setInputBonusValueMasked] = useState(
    bonusValue ? applyMaskToCurrencyValue(bonusValue.toString()) : "R$ 0,00"
  );
  const [usingBonus, setUsingBonus] = useState(bonusValue ? true : false);
  const [redeemedPoints, setRedeemedPoints] = useState<FidelityOptions>();

  // Calculations
  const { totalBonus, discountValue, discountPercentage } = useMemo(() => {
    const totalBonus = availableBonus || mockFidelityDataInfo.availableBonus;

    setDiscountedTransactionValue(transactionValue - Number(inputBonusValue));

    return {
      totalBonus,
      discountValue: Number(inputBonusValue),
      discountPercentage: (Number(inputBonusValue) / transactionValue) * 100
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputBonusValue, transactionValue, redeemedPoints, availableBonus]);

  // Handlers
  const handleOnSubmitForm = () => {
    setBonusValue(Number(inputBonusValue));
    navigateToNext();
  };

  const handleRedeem = (points: number) => {
    const redeemedOption = mockFidelityOptions.find(
      (option) => option.points === points
    );
    if (redeemedOption) {
      setRedeemedPoints(redeemedOption);
      setAvailableBonus(
        mockFidelityDataInfo.availableBonus + redeemedOption.value
      );
      modalRef.current?.close();
    }
  };

  const handleSkipRedeem = () => {
    modalRef.current?.close();
  };

  const handleInputChange = (value: string) => {
    if (value === "") {
      setInputBonusValue("0");
      setInputBonusValueMasked("R$ 0,00");
      return;
    }
    const numberValue = Number(removeMaskToCurrencyValue(value).toFixed(2));
    const maskedValue = applyMaskToCurrencyValue(numberValue.toString());

    if (numberValue > transactionValue && totalBonus > transactionValue) {
      setInputBonusValue(String(transactionValue - 1));
      setInputBonusValueMasked(
        applyMaskToCurrencyValue(String(transactionValue - 1))
      );
      return;
    }

    if (numberValue > totalBonus) {
      setInputBonusValue(String(totalBonus));
      setInputBonusValueMasked(applyMaskToCurrencyValue(String(totalBonus)));
      return;
    }

    setInputBonusValue(numberValue.toString());
    setInputBonusValueMasked(maskedValue);
  };

  const handleToggleBonus = () => setUsingBonus(!usingBonus);

  return {
    // Layout
    isMobile,
    modalRef,

    // Values
    transactionValue,
    inputBonusValue,
    inputBonusValueMasked,
    totalBonus,
    discountPercentage,
    discountValue,
    discountedTransactionValue,
    availableBonus,
    title: currentRouteConfig.title,
    isFidelityOnly: flowType === TransactionFlowType.BONUS_RETRIEVAL,
    redeemedPoints,

    // State
    usingBonus,

    // Mock Data
    mockFidelityDataInfo,
    mockFidelityOptions,

    // Handlers
    handleBack: navigateBack,

    handleOnSubmitForm,
    handleToggleBonus,
    handleRedeem,
    handleSkipRedeem,
    handleInputChange
  };
};
